import React, { FC } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { deepEqual } from 'fast-equals';
// fetch
import { updateOptionCategory, createOptionCategory } from '../../../../fetch';
import { Fade, Stack } from '@mui/material';
import { IOptionCategory } from '../../../../models';
import { yupRequiredString } from '../../../../helpers';
import { TextField, ModalSaveSection, Modal, Loader } from '../../../../components';

interface IAddEditOptionCategory {
  open: boolean;
  onClose: () => void;
  currentCategory?: IOptionCategory | null;
  fetchData: () => void;
}

const Schema = Yup.object().shape({
  categoryName: yupRequiredString(),
  description: yupRequiredString()
});

export const AddEditOptionCategory: FC<IAddEditOptionCategory> = ({
  open,
  onClose,
  currentCategory,
  fetchData
}) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          categoryName: currentCategory?.categoryName ?? '',
          description: currentCategory?.description ?? ''
        }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          try {
            currentCategory
              ? await updateOptionCategory(currentCategory?.categoryName, {
                  description: values.description
                })
              : await createOptionCategory(values);
            enqueueSnackbar(
              currentCategory
                ? 'Successfully updated option category!'
                : 'Successfully created option category!',
              {
                variant: 'success'
              }
            );
            onClose();
            fetchData();
            actions.resetForm();
          } catch (error: any) {
            enqueueSnackbar(error?.Detail ?? 'Error saving option category, please try again.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          initialValues
        }) => {
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                onClose();
              } else {
                return;
              }
            } else {
              onClose();
            }
          };
          return (
            <Modal
              open={open}
              onClose={() => {
                handleClose();
              }}
              maxWidth="sm"
              title={currentCategory ? 'Edit Option Category' : 'Add Option Category'}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Stack gap={1} mt={1}>
                    <TextField
                      disabled={!!currentCategory}
                      label="Category Name"
                      name="categoryName"
                      required
                    />
                    <TextField label="Description" name="description" required multiline rows={3} />
                  </Stack>
                  <ModalSaveSection
                    handleCancel={() => {
                      handleClose();
                    }}
                    isSaveDisabled={!dirty || isSubmitting || !isValid}
                  />
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
